import '../../../../Components/Form/form.css'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useRef } from 'react'
import { Controller, useForm } from 'react-hook-form'

import { DateField, NumberField, TextField } from '../../../../Components/Form'
import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import { getElementsByService, getServiceCount, isGoelettForm, writeKey } from '../Functions'
import { SectionShell } from '../SectionShell'
import { useGigiFormValidation } from '../ValidationContext'
import { getDiffStatus } from './Functions'
import { buildTransferDefaults, buildTransferSchema, TRANSFER_LOCATIONS } from './Transfer.schema'

const language = cap.language.state || 'IT'
const t = translations[language]

const SECTION_ID = 'transfer'

const LABELS = {
  departureDate: t.gfTransferDepartureDate,
  arrivalDate: t.gfTransferReturnDate,
  departurePlace: t.gfTransferDeparturePlace,
  arrivalPlace: t.gfTransferDestinationPlace,
  numPax: t.gfTransferNumPax
}
const label = (k) => LABELS[k] ?? k

const parseDate = (str) => {
  if (!str) return null
  const [d, m, y] = String(str).split('/')
  if (!d || !m || !y) return null
  const date = new Date(Number(y), Number(m) - 1, Number(d))
  return Number.isNaN(date.getTime()) ? null : date
}

const DIFF_TO_STATE = { new: 'new', changed: 'changed' }
const { transfer: transferFieldDefs } = getElementsByService()

/**
 * Evidenziazione visiva diff Nora sui campi trasferta in GigiForm.
 */
const TRANSFER_DIFF_HIGHLIGHT_ENABLED = true

/**
 * Restituisce diffStatus per FieldWrapper se l'evidenziazione è abilitata, altrimenti undefined.
 * @param {Object} fieldDef - Definizione campo da getElementsByService
 * @param {Array} diff - Elenco diff Nora per la sezione transfer
 * @returns {'new'|'changed'|undefined}
 */
const resolveTransferDiffStatus = (fieldDef, diff) => {
  if (!TRANSFER_DIFF_HIGHLIGHT_ENABLED) return undefined
  return DIFF_TO_STATE[getDiffStatus(fieldDef, diff)]
}

const SectionInner = ({
  state,
  setState,
  readOnly,
  enableMultiPax,
  departurePlaceRequired,
  isGoelett,
  validateOnMount,
  diff
}) => {
  const { registerSection, reportErrors, sectionHasErrors } = useGigiFormValidation()

  const schema = useMemo(
    () => buildTransferSchema({ departurePlaceRequired, enableMultiPax, isGoelett }),
    [departurePlaceRequired, enableMultiPax, isGoelett]
  )
  const transferDefaults = useMemo(() => buildTransferDefaults(state), [state])
  const hasMissingRequired = useMemo(() => {
    const { departureDate, arrivalDate, departurePlace, arrivalPlace } = transferDefaults
    if (isGoelett) return false
    if (!departureDate || !arrivalDate || !arrivalPlace?.trim()) return true
    if (departurePlaceRequired && !departurePlace?.trim()) return true
    return false
  }, [transferDefaults, departurePlaceRequired, isGoelett])

  const {
    control,
    trigger,
    setFocus,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: transferDefaults,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  const resetRef = useRef(reset)
  resetRef.current = reset
  const triggerRef = useRef(trigger)
  triggerRef.current = trigger

  // Allinea RHF allo stato Gigi quando arrivano dati da server (stesso id processo / ref mutata).
  useEffect(() => {
    const keepErrors = validateOnMount && hasMissingRequired
    resetRef.current(transferDefaults, { keepErrors })
  }, [transferDefaults, validateOnMount, hasMissingRequired])

  /**
   * Mostra errori quando mancano dati obbligatori (refresh, fine processamento Nora senza dati).
   * Non rivalidare se i campi sono già compilati, per non disturbare la modifica utente.
   */
  useEffect(() => {
    if (!validateOnMount || !hasMissingRequired) return
    const id = setTimeout(() => void triggerRef.current(), 0)
    return () => clearTimeout(id)
  }, [validateOnMount, hasMissingRequired, transferDefaults])

  const stateRef = useRef(state)
  stateRef.current = state

  const persistField = (location, value) => {
    const updated = writeKey(stateRef.current, location, undefined, value ?? '')
    if (Object.keys(updated).length) setState(updated)
  }
  const persistNumPax = (value) => {
    const numeric = value === '' || value === null ? '' : Number(value)
    const updated = writeKey(stateRef.current, TRANSFER_LOCATIONS.numPax, undefined, numeric)
    if (Object.keys(updated).length) setState(updated)
  }

  const errorCount = Object.keys(errors).length
  const errorsRef = useRef(errors)
  errorsRef.current = errors
  const errorCountRef = useRef(errorCount)
  errorCountRef.current = errorCount

  useEffect(() => {
    reportErrors(SECTION_ID, errorCount)
  }, [errorCount, reportErrors])

  useEffect(() => {
    return registerSection(SECTION_ID, {
      isValid: async () => trigger(),
      focusFirstError: () => {
        const firstKey = Object.keys(errorsRef.current)[0]
        if (!firstKey) return false
        const node = document.querySelector(`[data-field="${firstKey}"]`)
        if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' })
        try {
          setFocus(firstKey)
        } catch {
          /* no-op */
        }
        return true
      },
      hasErrors: () => errorCountRef.current > 0
    })
  }, [registerSection, trigger, setFocus])

  const departureDateValue = watch('departureDate')
  const minDateArrival = useMemo(
    () => parseDate(departureDateValue) || new Date(),
    [departureDateValue]
  )

  return (
    <SectionShell
      id={SECTION_ID}
      icon="map"
      title={t.datiTrasferta?.toUpperCase() || 'DATI TRASFERTA'}
      storageKey="gigiForm.section.transfer"
      hasErrors={sectionHasErrors(SECTION_ID)}
      contentId="transfer-grid"
    >
      <div className="ff-grid">
        <div className="ff-grid__item">
          <Controller
            name="departureDate"
            control={control}
            render={({ field, fieldState }) => (
              <DateField
                id="departureDate"
                label={label('departureDate')}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v)
                  persistField(TRANSFER_LOCATIONS.departureDate, v)
                }}
                onBlur={field.onBlur}
                required={!isGoelett}
                disabled={readOnly}
                error={fieldState.error?.message}
                diffStatus={resolveTransferDiffStatus(transferFieldDefs.departureDate, diff)}
                minDate={new Date()}
              />
            )}
          />
        </div>
        <div className="ff-grid__item">
          <Controller
            name="arrivalDate"
            control={control}
            render={({ field, fieldState }) => (
              <DateField
                id="arrivalDate"
                label={label('arrivalDate')}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v)
                  persistField(TRANSFER_LOCATIONS.arrivalDate, v)
                }}
                onBlur={field.onBlur}
                required={!isGoelett}
                disabled={readOnly}
                error={fieldState.error?.message}
                diffStatus={resolveTransferDiffStatus(transferFieldDefs.arrivalDate, diff)}
                minDate={minDateArrival}
              />
            )}
          />
        </div>
        <div className="ff-grid__item">
          <Controller
            name="departurePlace"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                id="departurePlace"
                label={label('departurePlace')}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v)
                  persistField(TRANSFER_LOCATIONS.departurePlace, v)
                }}
                onBlur={field.onBlur}
                required={departurePlaceRequired && !isGoelett}
                disabled={readOnly}
                error={fieldState.error?.message}
                diffStatus={resolveTransferDiffStatus(transferFieldDefs.departurePlace, diff)}
              />
            )}
          />
        </div>
        <div className="ff-grid__item">
          <Controller
            name="arrivalPlace"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                id="arrivalPlace"
                label={label('arrivalPlace')}
                value={field.value}
                onChange={(v) => {
                  field.onChange(v)
                  persistField(TRANSFER_LOCATIONS.arrivalPlace, v)
                }}
                onBlur={field.onBlur}
                required={!isGoelett}
                disabled={readOnly}
                error={fieldState.error?.message}
                diffStatus={resolveTransferDiffStatus(transferFieldDefs.arrivalPlace, diff)}
              />
            )}
          />
        </div>
        {!!enableMultiPax && (
          <div className="ff-grid__item">
            <Controller
              name="numPax"
              control={control}
              render={({ field, fieldState }) => (
                <NumberField
                  id="numPax"
                  label={label('numPax')}
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v)
                    persistNumPax(v)
                  }}
                  onBlur={field.onBlur}
                  disabled={readOnly}
                  error={fieldState.error?.message}
                  maxLength={3}
                />
              )}
            />
          </div>
        )}
      </div>
    </SectionShell>
  )
}

export const TransferSection = ({
  state: [state, setState],
  readOnly,
  enableMultiPax,
  validateOnMount = !readOnly,
  diff = []
}) => {
  const departurePlaceRequired =
    !!getServiceCount(state, 'plane') || !!getServiceCount(state, 'train')
  const isGoelett = isGoelettForm(state)
  return (
    <SectionInner
      key={readOnly ? 'readonly' : 'editable'}
      state={state}
      setState={setState}
      readOnly={readOnly}
      enableMultiPax={enableMultiPax}
      departurePlaceRequired={departurePlaceRequired}
      isGoelett={isGoelett}
      validateOnMount={validateOnMount}
      diff={diff}
    />
  )
}
